import * as Validator from "yup"
import { IHotel } from "./store"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	Alert,
	Box,
	Button,
	Col,
	Grid,
	Inline,
	Stack,
} from "@sembark-travel/ui-base"
import {
	EmptyNumberValidator,
	Form,
	GetFieldValue,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { SelectRoomTypes } from "../RoomTypes"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

interface IQuickAddRoomTypeProps {
	open: boolean
	hotel: IHotel
	onCancel: () => void
	onSuccess?: (hotel: IHotel, name?: string) => void
	name?: string
}

const quickAddRoomTypeSchema = Validator.object({
	name: Validator.object({
		name: Validator.string().trim(),
	})
		.required("Please select or enter a name")
		.test(
			"has-name",
			"Please select or enter a name",
			(value) => !!value?.name?.trim()
		),

	allowed_extra_beds: EmptyNumberValidator()
		.integer("Allowed extra beds should be an integer")
		.min(0, "Allowed extra beds should not be negative")
		.max(200, "Can not be greater than 200")
		.required("Allowed extra beds field is required"),

	allowed_adults_with_extra_bed: EmptyNumberValidator()
		.integer("Allowed adult extra beds per room should be an integer")
		.min(0, "Allowed adult extra beds per room should not be negative")
		.max(200, "Can not be greater than 200"),

	allowed_children_with_extra_bed: EmptyNumberValidator()
		.integer("Allowed child extra beds per room should be an integer")
		.min(0, "Allowed child extra beds per room should not be negative")
		.max(200, "Can not be greater than 200"),

	allowed_children_without_extra_bed: EmptyNumberValidator()
		.integer("Allowed CNBs per room should be an integer")
		.min(0, "Allowed CNBs per room should not be negative")
		.max(200, "Can not be greater than 200"),

	no_of_rooms: EmptyNumberValidator()
		.integer("Number of rooms should be an integer")
		.min(1, "Number of rooms should be at least 1")
		.max(1000, "Can not be greater than 1000"),
}).required("Room Type is required")

type QuickAddRoomTypeFormValues = Validator.InferType<
	typeof quickAddRoomTypeSchema
>

export function AddRoomTypeToHotel({
	open,
	hotel,
	onCancel,
	onSuccess,
	name,
}: IQuickAddRoomTypeProps) {
	const xhr = useXHR()

	const initialValues: QuickAddRoomTypeFormValues = {
		name: name ? { name } : {},
		allowed_extra_beds: 1,
		allowed_adults_with_extra_bed: 1,
		allowed_children_with_extra_bed: 1,
	}

	return (
		<Dialog open={open} onClose={onCancel} md>
			<Dialog.Header closeButton>
				<Dialog.Title>Quick Add Room Type</Dialog.Title>
			</Dialog.Header>

			<Form<QuickAddRoomTypeFormValues>
				initialValues={initialValues}
				validate={validateFormValues(quickAddRoomTypeSchema)}
				onSubmit={withServerErrors(async (values) => {
					if (!values.name?.name?.trim()) {
						throw new Error("Please select a room type")
					}

					const response = await xhr.patch(`/hotels/${hotel.id}/room-types`, {
						name: values.name.name,
						allowed_extra_beds: values.allowed_extra_beds,
						allowed_adults_with_extra_bed: values.allowed_adults_with_extra_bed,
						allowed_children_with_extra_bed:
							values.allowed_children_with_extra_bed,
						allowed_children_without_extra_bed:
							values.allowed_children_without_extra_bed,
						no_of_rooms: values.no_of_rooms,
					})

					if (response.data.data) {
						onSuccess?.(response.data.data, values.name.name)
					}

					showSnackbar("Room Type added successfully!")
					onCancel()
				})}
				subscription={{
					submitting: true,
				}}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body paddingX="0">
							<Stack gap="4">
								<QuickAddRoomTypeFields />
								<SubmissionError />
							</Stack>
						</Dialog.Body>

						<Dialog.Footer>
							<Inline gap="4" paddingX="6">
								<Button type="submit" disabled={submitting}>
									{submitting ? "Saving..." : "Save Room Type"}
								</Button>

								<Button type="button" onClick={onCancel} disabled={submitting}>
									Cancel
								</Button>
							</Inline>
						</Dialog.Footer>
					</form>
				)}
			</Form>
		</Dialog>
	)
}

function QuickAddRoomTypeFields() {
	return (
		<Stack paddingX="2" gap="4">
			<Box paddingX="4">
				<GetFieldValue<{ id?: string; name?: string }> name="name">
					{({ value }) => (
						<SelectField
							select={SelectRoomTypes}
							name="name"
							creatable
							label="Room Type"
							fetchOnMount
							fullWidth
							filterOptions={(arr) => {
								// Exclude the initially selected name passed from the parent to prevent it from appearing twice in the options.
								if (!arr?.length) return []

								if (!value?.id && value?.name) {
									return arr.filter((option) => option.name !== value.name)
								}

								return arr
							}}
						/>
					)}
				</GetFieldValue>
			</Box>

			<Grid>
				<Col xs={12} md={6}>
					<TextInputField
						label="Allowed extra bed(s)"
						type="number"
						name="allowed_extra_beds"
						min={0}
						max={1000}
						placeholder="e.g. 1"
					/>
				</Col>

				<Col xs={12} md={6}>
					<TextInputField
						label="No. of Rooms"
						type="number"
						name="no_of_rooms"
						min={1}
						max={1000}
						placeholder="e.g. 25"
						secondaryLabel="optional"
					/>
				</Col>
			</Grid>

			<Grid>
				<Col xs={12} md={4}>
					<TextInputField
						label="AWEB(s)"
						type="number"
						name="allowed_adults_with_extra_bed"
						min={0}
						max={1000}
						placeholder="e.g. 1"
						secondaryLabel="optional"
					/>
				</Col>

				<Col xs={12} md={4}>
					<TextInputField
						label="CWEB(s)"
						type="number"
						name="allowed_children_with_extra_bed"
						min={0}
						max={1000}
						placeholder="e.g. 1"
						secondaryLabel="optional"
					/>
				</Col>

				<Col xs={12} md={4}>
					<TextInputField
						label="CNB(s)"
						type="number"
						name="allowed_children_without_extra_bed"
						min={0}
						max={1000}
						placeholder="e.g. 0"
						secondaryLabel="optional"
					/>
				</Col>
			</Grid>

			<Box paddingX="4">
				<Alert>
					The configuration will be added for new Room Types and ignored for
					existing ones.
				</Alert>
			</Box>
		</Stack>
	)
}
